import { CSSProperties, useEffect } from "react";
import { useVisualViewport } from "../../../infrastructure/hooks/useVisualViewport";
import styles from "../AppDialog.module.scss";

export interface IKeyboardAwareDialog {
  /** Inline style for the dialog panel: pins it inside the visible area while a keyboard is up. */
  dialogStyle: CSSProperties | undefined;
  /** Mask class that top-aligns the panel while a keyboard is up (centering would push it under the keyboard). */
  maskClassName: string | undefined;
  isKeyboardOpen: boolean;
  /** Height of the visible area — for anything inside the dialog that sizes itself to the screen. */
  visibleHeight: number;
}

/**
 * Keeps a dialog fully on screen while the mobile keyboard is open. iOS Safari never
 * resizes the layout viewport for the keyboard — it pans the visual viewport instead —
 * so a centred fixed dialog ends up half under the keyboard with a dark band below it.
 * While the keyboard is up we (1) cap the panel to the visible height, (2) offset it by
 * the pan distance so it sits exactly in the visible window, and (3) keep the document
 * itself from being scrolled. When the keyboard closes everything is released.
 */
export const useKeyboardAwareDialog = (enabled: boolean): IKeyboardAwareDialog => {
  const { height, offsetTop, isKeyboardOpen } = useVisualViewport();
  const active = enabled && isKeyboardOpen;

  useEffect(() => {
    if (!active) return;
    // A focused input can still nudge the document itself on some iOS versions; the
    // panel is positioned from the visual-viewport offset, so the document must stay put.
    window.scrollTo(0, 0);
  }, [active, offsetTop, height]);

  return {
    dialogStyle: active
      ? { maxHeight: height, marginTop: offsetTop, marginBottom: 0 }
      : undefined,
    maskClassName: active ? styles.dialog_mask_keyboard : undefined,
    isKeyboardOpen: active,
    visibleHeight: height,
  };
};
